import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { ArrowLeft, TrendingUp, TrendingDown, AlertCircle } from "lucide-react";
import { useProjectPhaseStore, useProjectUtilityStore } from "../../../store/utilitySystemsStore";
import { ProjectPhase } from "../../../types/api";

const ProjectPhaseDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { phases, phasesLoading, fetchPhases } = useProjectPhaseStore();
  const { projects, fetchProjects } = useProjectUtilityStore();
  const [selectedProject, setSelectedProject] = useState<string>("");

  useEffect(() => {
    fetchPhases();
    if (!projects.length) fetchProjects();
  }, [fetchPhases, fetchProjects, projects.length]);

  const projectsMap = useMemo(() => {
    const map = new Map<string, string>();
    projects.forEach((project) => {
      const id = project.id || project._id;
      if (id) map.set(id, project.name);
    });
    return map;
  }, [projects]);

  const filteredPhases = useMemo(() => {
    if (selectedProject) {
      return phases.filter((phase) => phase.projectId === selectedProject);
    }
    return phases;
  }, [phases, selectedProject]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate summary statistics
  const summary = useMemo(() => {
    const totalEstimated = filteredPhases.reduce(
      (sum, phase) => sum + (phase.estimatedCost || 0),
      0
    );
    const totalActual = filteredPhases.reduce(
      (sum, phase) => sum + (phase.actualCost || 0),
      0
    );
    const totalVariance = totalActual - totalEstimated;
    const overBudgetCount = filteredPhases.filter(
      (phase) => phase.status === "Over Budget"
    ).length;
    const onTrackCount = filteredPhases.filter(
      (phase) => phase.status === "On Track"
    ).length;
    const underBudgetCount = filteredPhases.filter(
      (phase) => phase.status === "Under Budget"
    ).length;

    return {
      totalEstimated,
      totalActual,
      totalVariance,
      overBudgetCount,
      onTrackCount,
      underBudgetCount,
      totalPhases: filteredPhases.length,
    };
  }, [filteredPhases]);

  // Prepare data for bar chart (Budget vs Actual by Phase)
  const barChartData = useMemo(() => {
    return filteredPhases.map((phase) => ({
      name: phase.name?.substring(0, 20) || "N/A",
      estimated: phase.estimatedCost || 0,
      actual: phase.actualCost || 0,
      project: projectsMap.get(phase.projectId) || "N/A",
    }));
  }, [filteredPhases, projectsMap]);

  // Prepare data for pie chart (Status distribution)
  const pieChartData = useMemo(() => {
    const statusCounts = {
      "Not Started": 0,
      "On Track": 0,
      "Over Budget": 0,
      "Under Budget": 0,
    };

    filteredPhases.forEach((phase) => {
      const status = phase.status || "Not Started";
      if (status in statusCounts) {
        statusCounts[status as keyof typeof statusCounts]++;
      }
    });

    return [
      { name: "On Track", value: statusCounts["On Track"], color: "#4CAF50" },
      {
        name: "Over Budget",
        value: statusCounts["Over Budget"],
        color: "#F44336",
      },
      {
        name: "Under Budget",
        value: statusCounts["Under Budget"],
        color: "#2196F3",
      },
      {
        name: "Not Started",
        value: statusCounts["Not Started"],
        color: "#9E9E9E",
      },
    ].filter((item) => item.value > 0);
  }, [filteredPhases]);

  // Prepare data for project-wise summary
  const projectSummaryData = useMemo(() => {
    const projectMap = new Map<string, { estimated: number; actual: number; phases: number }>();

    filteredPhases.forEach((phase) => {
      const projectName = projectsMap.get(phase.projectId) || "Unknown";
      const existing = projectMap.get(projectName) || {
        estimated: 0,
        actual: 0,
        phases: 0,
      };
      projectMap.set(projectName, {
        estimated: existing.estimated + (phase.estimatedCost || 0),
        actual: existing.actual + (phase.actualCost || 0),
        phases: existing.phases + 1,
      });
    });

    return Array.from(projectMap.entries()).map(([name, data]) => ({
      name: name.substring(0, 20),
      estimated: data.estimated,
      actual: data.actual,
      variance: data.actual - data.estimated,
      phases: data.phases,
    }));
  }, [filteredPhases, projectsMap]);

  const COLORS = ["#4CAF50", "#F44336", "#2196F3", "#9E9E9E"];

  return (
    <div className="px-2 py-4 mt-14 w-full h-full">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/utility-systems/budgeting-cost-control")}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Back to List"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-semibold text-gray-800">
            Budgeting & Cost Control Dashboard
          </h1>
        </div>
        <select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          className="border-2 border-[#4f5762] px-4 py-2 rounded font-semibold text-[#4e545d]"
        >
          <option value="">All Projects</option>
          {projects.map((project) => {
            const id = project.id || project._id;
            return (
              <option key={id} value={id}>
                {project.name}
              </option>
            );
          })}
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Estimated</p>
              <p className="text-2xl font-bold text-gray-800">
                {formatCurrency(summary.totalEstimated)}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <TrendingUp className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Actual</p>
              <p className="text-2xl font-bold text-gray-800">
                {formatCurrency(summary.totalActual)}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <TrendingDown className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Variance</p>
              <p
                className={`text-2xl font-bold ${
                  summary.totalVariance > 0 ? "text-red-600" : "text-green-600"
                }`}
              >
                {formatCurrency(summary.totalVariance)}
              </p>
            </div>
            <div
              className={`p-3 rounded-full ${
                summary.totalVariance > 0 ? "bg-red-100" : "bg-green-100"
              }`}
            >
              {summary.totalVariance > 0 ? (
                <TrendingUp className="text-red-600" size={24} />
              ) : (
                <TrendingDown className="text-green-600" size={24} />
              )}
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Over Budget Phases</p>
              <p className="text-2xl font-bold text-red-600">
                {summary.overBudgetCount}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                of {summary.totalPhases} total
              </p>
            </div>
            <div className="bg-red-100 p-3 rounded-full">
              <AlertCircle className="text-red-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Budget vs Actual Bar Chart */}
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Budget vs Actual Cost by Phase
          </h3>
          {phasesLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading chart data...</div>
            </div>
          ) : barChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis />
                <Tooltip
                  formatter={(value: number) => formatCurrency(value)}
                />
                <Legend />
                <Bar dataKey="estimated" fill="#3B82F6" name="Estimated" />
                <Bar dataKey="actual" fill="#10B981" name="Actual" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">No data available</div>
            </div>
          )}
        </div>

        {/* Status Distribution Pie Chart */}
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Phase Status Distribution
          </h3>
          {phasesLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading chart data...</div>
            </div>
          ) : pieChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color || COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">No data available</div>
            </div>
          )}
        </div>
      </div>

      {/* Project-wise Summary */}
      <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Project-wise Budget Summary
        </h3>
        {phasesLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Loading chart data...</div>
          </div>
        ) : projectSummaryData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={projectSummaryData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis
                dataKey="name"
                type="category"
                width={150}
              />
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
              />
              <Legend />
              <Bar dataKey="estimated" fill="#3B82F6" name="Estimated" />
              <Bar dataKey="actual" fill="#10B981" name="Actual" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">No data available</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectPhaseDashboard;

